import React, { useState } from 'react';
import { PendingDepositAction } from '../services/geolinkApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  deposit: PendingDepositAction | null;
  onApprove: (deposit: PendingDepositAction) => Promise<void>;
  onDecline: (deposit: PendingDepositAction) => Promise<void>;
  isProcessing?: boolean;
}

type Stage = 'review' | 'signing' | 'submitting' | 'success' | 'error';

export const PendingDepositOverlay: React.FC<Props> = ({
  isOpen, onClose, deposit, onApprove, onDecline, isProcessing = false,
}) => {
  const [stage, setStage] = useState<Stage>('review');
  const [errorMsg, setErrorMsg] = useState('');

  if (!isOpen || !deposit) return null;

  // ── helpers ──
  const xlmAmount = deposit.parameters.amount
    ? (parseInt(deposit.parameters.amount, 10) / 10_000_000).toFixed(7)
    : null;
  const asset = deposit.parameters.asset || 'XLM';

  const timeLeft = (() => {
    try {
      const ms = new Date(deposit.expires_at).getTime() - Date.now();
      if (ms <= 0) return 'Expired';
      const m = Math.floor(ms / 60000);
      if (m < 60) return `${m} min${m !== 1 ? 's' : ''}`;
      return `${Math.floor(m / 60)}h ${m % 60}m`;
    } catch { return '—'; }
  })();

  const isExpired = new Date(deposit.expires_at).getTime() <= Date.now();

  const statusColor = {
    pending: '#f59e0b',
    in_progress: '#3b82f6',
    completed: '#22c55e',
    failed: '#ef4444',
    cancelled: '#64748b',
  }[deposit.status] ?? '#64748b';

  // ── approve flow ──
  const handleApprove = async () => {
    setStage('signing');
    setErrorMsg('');
    try {
      setStage('submitting');
      await onApprove(deposit);
      setStage('success');
    } catch (e: any) {
      setErrorMsg(e?.message || 'Transaction failed');
      setStage('error');
    }
  };

  const handleDecline = async () => {
    try {
      await onDecline(deposit);
      onClose();
    } catch (e: any) {
      setErrorMsg(e?.message || 'Failed to decline');
    }
  };

  const reset = () => { setStage('review'); setErrorMsg(''); };

  // ── backdrop ──
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.65)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#0f1420', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '14px', width: '100%', maxWidth: '400px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#f0f4ff' }}>
              Pending Deposit
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>
              {deposit.rule_name || deposit.contract_name || 'Smart Contract Deposit'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}
          >×</button>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '16px 18px' }}>

          {/* Stage: review */}
          {(stage === 'review' || stage === 'error') && (
            <>
              {/* Amount card */}
              {xlmAmount && (
                <div style={{
                  background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
                  borderRadius: '10px', padding: '14px 16px', marginBottom: '14px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: '11px', color: '#818cf8', marginBottom: '4px' }}>Deposit Amount</div>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#e2e8f0', letterSpacing: '-0.5px' }}>
                    {xlmAmount} <span style={{ fontSize: '16px', color: '#818cf8' }}>{asset}</span>
                  </div>
                </div>
              )}

              {/* Details */}
              {[
                ['Status', <span style={{ background: `${statusColor}22`, color: statusColor, padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 700 }}>{deposit.status}</span>],
                ['Contract', deposit.contract_name],
                ['Function', <code style={{ fontSize: '11px', color: '#a78bfa' }}>{deposit.function_name}</code>],
                deposit.location ? ['Location', `${deposit.location.latitude.toFixed(4)}, ${deposit.location.longitude.toFixed(4)}`] : null,
                ['Expires', <span style={{ color: isExpired ? '#f87171' : '#94a3b8' }}>{timeLeft}</span>],
                ['Received', new Date(deposit.received_at).toLocaleString()],
              ].filter(Boolean).map(([label, value]: any) => (
                <div key={label} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '12px',
                }}>
                  <span style={{ color: '#64748b' }}>{label}</span>
                  <span style={{ color: '#e2e8f0', textAlign: 'right', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
                </div>
              ))}

              {/* Extra params */}
              {Object.entries(deposit.parameters)
                .filter(([k]) => !['amount', 'asset', 'user_address'].includes(k))
                .slice(0, 4)
                .map(([k, v]) => (
                  <div key={k} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', fontSize: '12px',
                  }}>
                    <span style={{ color: '#64748b' }}>{k}</span>
                    <span style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: '11px' }}>{String(v)}</span>
                  </div>
                ))
              }

              {/* Error */}
              {stage === 'error' && (
                <div style={{
                  marginTop: '12px', background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px',
                  padding: '10px 12px', fontSize: '12px', color: '#f87171',
                }}>
                  ⚠ {errorMsg}
                  <button onClick={reset} style={{ background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', fontSize: '11px', display: 'block', marginTop: '6px' }}>Try again</button>
                </div>
              )}

              {/* Action buttons */}
              {deposit.status === 'pending' && !isExpired && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                  <button
                    onClick={handleDecline}
                    disabled={isProcessing}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '8px',
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                      color: '#94a3b8', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Decline
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={isProcessing}
                    style={{
                      flex: 2, padding: '10px', borderRadius: '8px',
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      border: 'none', color: '#fff', fontSize: '13px', fontWeight: 700,
                      cursor: 'pointer', boxShadow: '0 4px 14px rgba(99,102,241,0.4)',
                    }}
                  >
                    ✓ Approve Deposit
                  </button>
                </div>
              )}

              {isExpired && (
                <div style={{ marginTop: '14px', textAlign: 'center', color: '#f87171', fontSize: '12px' }}>
                  This deposit request has expired.
                </div>
              )}
            </>
          )}

          {/* Stage: signing */}
          {stage === 'signing' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: '36px', marginBottom: '14px' }}>🔑</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0', marginBottom: '8px' }}>Waiting for Signature</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Please approve the transaction in your wallet or with your passkey…</div>
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '6px' }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: '8px', height: '8px', borderRadius: '50%', background: '#6366f1',
                    animation: `pulse 1.2s ease-in-out ${i * 0.3}s infinite`,
                  }} />
                ))}
              </div>
            </div>
          )}

          {/* Stage: submitting */}
          {stage === 'submitting' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: '36px', marginBottom: '14px' }}>⛓️</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0', marginBottom: '8px' }}>Submitting to Blockchain</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Broadcasting transaction to the Stellar network…</div>
              <div style={{ marginTop: '20px' }}>
                <div style={{
                  width: '80%', height: '3px', background: 'rgba(255,255,255,0.08)',
                  borderRadius: '2px', margin: '0 auto', overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%', background: 'linear-gradient(90deg, #6366f1, #a78bfa)',
                    borderRadius: '2px', animation: 'slideBar 1.5s ease-in-out infinite',
                    width: '40%',
                  }} />
                </div>
              </div>
            </div>
          )}

          {/* Stage: success */}
          {stage === 'success' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: 'rgba(34,197,94,0.15)', border: '2px solid #22c55e',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '24px', margin: '0 auto 16px',
              }}>✓</div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#e2e8f0', marginBottom: '8px' }}>Deposit Approved!</div>
              {xlmAmount && (
                <div style={{ fontSize: '13px', color: '#22c55e', marginBottom: '6px' }}>
                  {xlmAmount} {asset} deposited
                </div>
              )}
              <div style={{ fontSize: '12px', color: '#64748b' }}>The transaction has been submitted successfully.</div>
              <button
                onClick={onClose}
                style={{
                  marginTop: '20px', padding: '9px 28px', borderRadius: '8px',
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                  color: '#e2e8f0', fontSize: '13px', cursor: 'pointer', fontWeight: 600,
                }}
              >Close</button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:.3;transform:scale(.8)} 50%{opacity:1;transform:scale(1)} }
        @keyframes slideBar { 0%{transform:translateX(-100%)} 100%{transform:translateX(350%)} }
      `}</style>
    </div>
  );
};
